import { EventRefPage } from '@vacationtracker/shared/types/core-event'

import { IUserFilteredInfo } from '../../../../types/users'
import { IGetLabelsShort } from '../../../../types/labels'
import { IOptimisticUserEventParams } from '../hooks/useOptimisticUserEvent'
import { IProfileColumnsEditingContext } from './profileColumns'

export interface ILocationDeleteSuggestion {
  locationId: string
  locationName: string
}

export interface ICreateEditingContextDeps {
  teams: { id: string; name: string }[]
  locations: { id: string; name: string }[]
  allLabels: IGetLabelsShort[]
  /** Read through a ref by the page, so these always see the current render's values. */
  isAnyRowSelected: () => boolean
  canEditCell: (row: IUserFilteredInfo, columnId: string) => boolean
  getLockReason: (row: IUserFilteredInfo, columnId: string) => string | undefined
  optimisticPost: (params: IOptimisticUserEventParams) => Promise<void>
  patchUser: (userId: string, patch: Partial<IUserFilteredInfo>) => void
  postEvent: (body: object, refPage: EventRefPage) => Promise<unknown>
  /** Omitted where offering to delete an emptied location makes no sense - the location's own page. */
  fetchLocationDeleteSuggestion?: (params: { locationId?: string; affectedUserWasActive: boolean }) => Promise<ILocationDeleteSuggestion | null>
  suggestLocationDelete?: (suggestion: ILocationDeleteSuggestion) => void
  /**
   * Set only where the list is scoped to one location: once a move is accepted the row no longer
   * belongs in it. Receives the destination so a move that lands back here is left alone. The Users
   * page omits it - the row belongs there whichever location it ends up in.
   */
  onLocationMoved?: (userId: string, newLocationId: string) => void
  onLabelError: (error: unknown) => void
  /** Warns what a location move does to the person's leave, before it is confirmed. Takes the
   * strings rather than an i18n id so the columns stay free of formatting. */
  locationChangeNote: (firstName: string, locationName: string) => string
}

/**
 * The commit handlers behind every inline cell edit, as a plain function of its dependencies.
 *
 * Pulled out of the page so both the Users page and a location's Users tab share one definition of
 * what an edit does - and so the handlers can be tested without mounting a table.
 */
export function createEditingContext(deps: ICreateEditingContextDeps): IProfileColumnsEditingContext {
  return {
    // A bulk selection owns the rows, so single-cell editing steps aside until it is cleared.
    canEditCell: (row: IUserFilteredInfo, columnId: string) => !deps.isAnyRowSelected() && deps.canEditCell(row, columnId),
    getLockReason: (row: IUserFilteredInfo, columnId: string) => (deps.isAnyRowSelected() ? 'users.table.lockBulkSelection' : deps.getLockReason(row, columnId)),
    teams: deps.teams,
    locations: deps.locations,
    allLabels: deps.allLabels,
    locationChangeNote: deps.locationChangeNote,

    commitTeam: async (user: IUserFilteredInfo, teamId: string) => {
      const newTeam = deps.teams.find((team) => team.id === teamId)
      if (!newTeam || !user.team) {
        return
      }
      await deps.optimisticPost({
        userId: user.id,
        eventBody: { eventType: 'TEAM_USER_MOVED', eventGroup: 'USER', teamId, userId: user.id },
        patch: { team: { id: newTeam.id, name: newTeam.name } },
        inversePatch: { team: user.team },
      })
    },

    commitLocation: async (user: IUserFilteredInfo, locationId: string) => {
      const newLocation = deps.locations.find((loc) => loc.id === locationId)
      if (!newLocation || !user.location) {
        return
      }
      // Kicked off before the move: the suggestion needs the pre-mutation active count
      const suggestionPromise =
        user.location.id !== locationId && deps.fetchLocationDeleteSuggestion
          ? deps.fetchLocationDeleteSuggestion({ locationId: user.location.id, affectedUserWasActive: user.status === 'ACTIVE' })
          : Promise.resolve(null)
      await deps.optimisticPost({
        userId: user.id,
        eventBody: { eventType: 'LOCATION_USER_MOVED', eventGroup: 'USER', locationId, userId: user.id },
        patch: { location: { id: newLocation.id, name: newLocation.name } },
        inversePatch: { location: user.location },
      })
      // Past the post, so a rejected move leaves the row where it was rather than needing to be put
      // back. Waiting for the event to be projected instead would leave the row sitting in a list it
      // has already left.
      deps.onLocationMoved?.(user.id, locationId)
      const suggestion = await suggestionPromise
      if (suggestion) {
        deps.suggestLocationDelete?.(suggestion)
      }
    },

    commitStartDate: async (user: IUserFilteredInfo, startDate: string) => {
      await deps.optimisticPost({
        userId: user.id,
        eventBody: { eventType: 'USER_UPDATED', eventGroup: 'USER', userId: user.id, name: user.name, startDate: new Date(startDate).toISOString() },
        patch: { startDate },
        inversePatch: { startDate: user.startDate },
      })
    },

    commitEmployeeId: async (user: IUserFilteredInfo, employeeId: string) => {
      await deps.optimisticPost({
        userId: user.id,
        eventBody: { eventType: 'USER_UPDATED', eventGroup: 'USER', userId: user.id, name: user.name, employeeId },
        patch: { employeeId },
        inversePatch: { employeeId: user.employeeId },
      })
    },

    commitBirthday: async (user: IUserFilteredInfo, birthday: string) => {
      await deps.optimisticPost({
        userId: user.id,
        eventBody: { eventType: 'USER_UPDATED', eventGroup: 'USER', userId: user.id, name: user.name, birthday },
        patch: { birthday },
        inversePatch: { birthday: user.birthday },
      })
    },

    toggleLabel: async (user: IUserFilteredInfo, label: IGetLabelsShort, assigned: boolean) => {
      const previousLabels = user.labels || []
      const nextLabels = assigned ? [...previousLabels, { id: label.id, name: label.name, color: label.color }] : previousLabels.filter((entry) => entry.id !== label.id)
      deps.patchUser(user.id, { labels: nextLabels })
      try {
        await deps.postEvent(
          assigned
            ? { eventType: 'LABEL_ASSIGNED_TO_USER', eventGroup: 'LABEL', labelId: label.id, name: label.name, userId: user.id }
            : { eventType: 'LABEL_REMOVED_FROM_USER', eventGroup: 'LABEL', labelId: label.id, userId: user.id },
          'users-table'
        )
      } catch (error) {
        deps.patchUser(user.id, { labels: previousLabels })
        deps.onLabelError(error)
        throw error
      }
    },
  }
}
